/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Observable} from 'rxjs';

import {HTMLElements} from '../utils/html-elements';

/**
 * Directive that makes an element or component 'sticky' when it's not in the viewport.
 *
 * Usage :
 *
 * <div class="alert alert-danger" gp-sticky [marginTop]="64">
 *   Stick to top with a 64px margin top
 * </div>
 *
 * <alert type="info" gp-sticky="bottom">
 *   Stick to bottom
 * </alert>
 */
@Directive({
  selector: '[gp-sticky]'
})
export class StickyDirective implements OnInit, OnDestroy {

  @Input()
  private refreshOn: Observable<any>;

  @Input()
  private marginTop: number = 0;

  @Input()
  private beginWith: number = 0;

  @Input('gp-sticky')
  private anchorPosition: string = 'top';

  // Whether or not the element is currently stuck
  private stuck: boolean = false;

  // The fix point at which to toggle the sticky behavior
  private fixPoint: number;

  // The original position of the element
  private originalPosition: string;

  // The original bottom of the element
  private originalBottom: string;

  // The original top position of the element
  private originalTop: string;

  // Placeholder element that will be used to 'replace' the sticky element so that it fills the same space
  private placeholder: HTMLElement;

  // Functions to call upon destroy of this directive (event listener stop functions)
  private callOnDestroy: Array<Function> = [];

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    this.placeholder = document.createElement('div');
    this.elementRef.nativeElement.parentNode.insertBefore(this.placeholder, this.elementRef.nativeElement.nextSibling);
  }

  public ngOnInit(): any {
    if (this.refreshOn !== undefined) {
      this.refreshOn.subscribe(() => setTimeout(() => this.initialize()));
    }
    setTimeout(this.initialize.bind(this));
  }

  public ngOnDestroy(): any {
    this.callOnDestroy.forEach(f => f.call(this));
  }

  /**
   * Initialization routine : perform setup and add event listeners
   */
  private initialize() {
    this.setup();

    this.callOnDestroy.push(this.renderer.listen('document', 'scroll', this.checkSticky.bind(this)));
    this.callOnDestroy.push(this.renderer.listen('window', 'resize', this.setup.bind(this)));
  }

  /**
   * Setup routine : compute fix point, save original position, and checks initial sticky status.
   */
  private setup() {
    if (this.stuck) {
      this.unStick();
    }

    if (this.anchorPosition === 'bottom') {
      this.fixPoint = window.pageYOffset + Math.ceil(this.elementRef.nativeElement.getBoundingClientRect().bottom);
    } else {
      this.fixPoint = window.pageYOffset
        + window.innerHeight
        + this.elementRef.nativeElement.getBoundingClientRect().top
        - this.elementRef.nativeElement.offsetHeight
        + this.marginTop;
    }

    this.originalPosition = this.elementRef.nativeElement.style.position;
    this.originalBottom = this.elementRef.nativeElement.style.bottom;
    this.originalTop = this.elementRef.nativeElement.style.top;

    this.checkSticky();
  }

  /**
   * Check whether or not we need to enable or disable the sticky behavior.
   */
  private checkSticky() {
    const scrollTop = window.pageYOffset + document.documentElement.clientHeight - this.beginWith;

    if (this.anchorPosition === 'bottom') {
      if (!this.stuck && scrollTop < this.fixPoint) {
        this.stick();
      } else if (this.stuck && scrollTop > this.fixPoint) {
        this.unStick();
      }
    } else {
      if (!this.stuck && scrollTop > this.fixPoint) {
        this.stick();
      } else if (this.stuck && scrollTop < this.fixPoint) {
        this.unStick();
      }
    }
  }

  /**
   * Enables the sticky behavior.
   */
  private stick() {
    if (this.stuck) {
      return;
    }
    const rectangle = this.elementRef.nativeElement.getBoundingClientRect();

    HTMLElements.setStyles(this.placeholder, {
      display: this.elementRef.nativeElement.style.display,
      width: `${rectangle.width}px`,
      height: `${rectangle.height}px`,
      cssFloat: this.elementRef.nativeElement.style.cssFloat
    });

    const stickStyles: any = {
      position: 'fixed',
      height: `${rectangle.height}px`,
      width: `${rectangle.width}px`,
      left: `${rectangle.left}px`
    };

    if (this.anchorPosition === 'bottom') {
      stickStyles.bottom = '0';
    } else {
      stickStyles.top = `${this.marginTop}px`;
    }

    HTMLElements.setStyles(this.elementRef.nativeElement, stickStyles);
    HTMLElements.setClasses(this.elementRef.nativeElement, 'sticky-active');

    this.stuck = true;
  }

  /**
   * Disable the sticky behavior
   */
  private unStick() {
    HTMLElements.setStyles(this.placeholder, {
      display: 'none'
    });

    HTMLElements.setStyles(this.elementRef.nativeElement, {
      position: this.originalPosition,
      bottom: this.originalBottom,
      top: this.originalTop,
      width: 'initial', // FIXME : support for saving an original width property
      left: 'initial'
    });

    this.elementRef.nativeElement.classList.remove('sticky-active');

    this.stuck = false;
  }
}
